/**
 * @fileoverview
 * Input Field Component - Reusable input field with form control support
 * Migrated to new architecture with OnPush change detection
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  Optional,
  Self,
  ChangeDetectionStrategy,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NgControl,
} from '@angular/forms';
import { TimePickerComponent } from '../time-picker/time-picker.component';

@Component({
  selector: 'app-input-field',
  standalone: true,
  imports: [CommonModule, FormsModule, TimePickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './input-field.component.html',
  styleUrl: './input-field.component.css',
})
export class InputFieldComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);

  @Input() label: string = '';
  @Input() type: string = 'text';
  @Input() placeholder: string = '';
  @Input() labelClass: string = '';
  @Input() showSearchIcon: boolean = false;
  @Input() accept: string = '';
  @Input() isReadOnly: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() showAsterisk: boolean = false;
  @Input() step: string = '';
  @Input() min: string = '';
  @Input() max: string = '';
  /** When type is "time", pass the selected date (e.g. from a date field) so time cannot exceed current time when date is today. */
  @Input() selectedDate: Date | string | null = null;
  /** When true and type is "number", only digits are allowed (cross-browser, fixes Firefox accepting letters). */
  @Input() integerOnly: boolean = false;
  @Input() errorMessage: string | null = null;
  @Input() showError: boolean = false;
  @Input() inputClass: string = '';
  /** Emits after the native input blurs (and marks the CVA as touched). */
  @Output() blurred = new EventEmitter<FocusEvent>();
  isLinkMode: boolean = false;
  attachedLink: string = '';
  value: string = '';

  onChange = (value: string) => {};
  onTouched = () => {};

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  // --- New Method for Date Picker ---
  openNativeDatePicker() {
    if (this.type === 'date') {
      const input = document.createElement('input');
      input.type = 'date';
      input.click();
      input.onchange = (e: any) => {
        this.value = e.target.value;
        this.onChange(this.value);
        input.remove();
      };
    }
  }

  // --- Method for time picker change ---
  onTimeChange(value: string) {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  // --- Existing Methods ---
  attachLink() {
    const link = prompt('Enter the file link:');
    if (link) {
      this.value = link;
      this.onChange(this.value);
    }
  }

  writeValue(value: string | number | null | undefined): void {
    this.value = value != null ? String(value) : '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /** Handle input: when type is number and integerOnly, restrict to digits only (fixes Firefox). */
  onInputEvent(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = target?.value ?? '';
    if (this.type === 'number' && this.integerOnly) {
      const digitsOnly = raw.replace(/\D/g, '');
      this.value = digitsOnly;
      if (target) target.value = digitsOnly;
      const num = digitsOnly === '' ? null : Number(digitsOnly);
      this.onChange(num as any);
      this.cdr.markForCheck();
      return;
    }
    this.onChange(this.value);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onBlurEvent(event: FocusEvent): void {
    this.onTouched();
    this.blurred.emit(event);
  }

  private _formDisabledState: boolean = false;

  setDisabledState(isDisabled: boolean): void {
    // Store form-controlled disabled state
    this._formDisabledState = isDisabled;
  }

  get effectiveDisabled(): boolean {
    // Use @Input() isDisabled if set, otherwise use value from setDisabledState
    return this.isDisabled || this._formDisabledState;
  }
}

